<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { defineAsyncComponent } from '@@/bits/vue'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import ProcessedImage from '@@/vuecomponents/processed_image.vue'
import { useSlideshowLoadingState } from '@@/vuecomposables/slideshow_loading_state'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

defineProps<{
  isCurrentlyViewed: boolean
}>()

const SurfaceTranslationBanner = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceTranslationBanner.vue'))

const WALL_ICON_SIZE = 72
const WALL_ICON_SIZE_BIG = 140

const { title, description, background, builder, portrait } = storeToRefs(useSurfaceStore())
const { isSmallerThanDesktop2XL, windowWidth, isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const { pageCoverLoaded } = useSlideshowLoadingState()
const handleCoverLoaded = () => {
  pageCoverLoaded.value = true
}

const {
  backgroundLuminanceBasedTailwindClasses,
  accentColorStyle,
  primaryColorStyle,
  textColorClass,
  shouldRemoveBackground,
  hasDarkBackgroundLuminance,
} = useSlideshowStyles()
const wallIconSize = computed(() => (isSmallerThanDesktop2XL.value ? WALL_ICON_SIZE : WALL_ICON_SIZE_BIG))

const surfaceTranslationsStore = useSurfaceTranslationsStore()
const { isDetectingLanguage, hasDismissedTranslationBanner, xTranslateToUserLocaleBanner, xRevertToOriginalBanner } =
  storeToRefs(surfaceTranslationsStore)

const xTranslationBanner = computed<boolean>(
  () =>
    // Only show the banner after the detection of language is completed
    !isDetectingLanguage.value &&
    !hasDismissedTranslationBanner.value &&
    (xRevertToOriginalBanner.value || xTranslateToUserLocaleBanner.value) &&
    !isSmallerThanTabletPortrait.value,
)
</script>

<template>
  <div :class="['h-full', 'flex flex-col']" :style="{ backgroundColor: primaryColorStyle }">
    <div class="flex-1 w-full overflow-hidden">
      <ImageThumbnail
        v-if="!shouldRemoveBackground"
        :src="background.url"
        :original-image-height="background.height"
        :original-image-width="background.width"
        :width="windowWidth"
        :alt="''"
        :process-image="true"
        :lazy-loading="!isCurrentlyViewed"
        resize-strategy="fit"
        @load="handleCoverLoaded()"
        @error="handleCoverLoaded()"
      />
    </div>
    <div
      :class="[
        'box-border',
        'relative',
        'pt-12 pb-6',
        'tablet-portrait:py-24',
        // ps/pe is not compatible with calc, so need to specify rtl explicitly
        'tablet-landscape:ps-19 tablet-landscape:pr-[calc(20vw+24px)] tablet-landscape:rtl:pl-[calc(20vw+24px)]', // goodcheck-disable-line
        'desktop:ps-22 tablet-landscape:pt-16 tablet-landscape:pb-8',
        'desktop-2xl:ps-57 desktop-2xl:pr-[calc(25vw+40px)] desktop-2xl:rtl:pl-[calc(25vw+40px)] desktop-2xl:pt-24 desktop-2xl:pb-12', // goodcheck-disable-line
        'desktop-3xl:ps-80 desktop-3xl:pr-[calc(25vw+80px)] desktop-3xl:rtl:pl-[calc(25vw+80px)] desktop-3xl:pt-30 desktop-3xl:pb-15', // goodcheck-disable-line
        'min-h-[33%] max-h-[67%]',
      ]"
      :style="{ backgroundColor: primaryColorStyle }"
    >
      <ProcessedImage
        v-if="portrait"
        :class="['absolute', 'max-tablet-landscape:start-0 max-tablet-landscape:end-0', 'max-tablet-landscape:mx-auto']"
        :style="{
          top: `-${wallIconSize / 2}px`,
        }"
        :src="portrait"
        :width="wallIconSize"
        :height="wallIconSize"
        :transforms="{ preset: 'icon', width: wallIconSize, height: wallIconSize }"
        :alt="__('Padlet icon')"
      />
      <div
        :class="[
          'mb-1.5 tablet-landscape:mb-3 desktop-3xl:mb-4',
          'text-body-small tablet-portrait:text-body desktop:text-body-large desktop-2xl:text-heading-2 desktop-3xl:text-heading-1',
          'text-center tablet-landscape:text-start',
          textColorClass,
        ]"
        :data-testid="'slideshowPageCoverBuilderName'"
      >
        {{ builder.name || builder.username }}
      </div>
      <SurfaceTranslationBanner
        v-if="isAppUsing('translatePadlet') && xTranslationBanner"
        class="absolute end-5 top-4"
        :button-classes="backgroundLuminanceBasedTailwindClasses"
        :is-dark-mode="hasDarkBackgroundLuminance"
      />
      <h1
        :class="[
          'break-words',
          'font-semibold',
          'text-center tablet-landscape:text-start',
          'mb-1.5 tablet-landscape:mb-2 desktop-3xl:mb-3',
          'mx-9 tablet-portrait:mx-36 tablet-landscape:mx-0',
          'text-slideshow-heading-md tablet-portrait:text-slideshow-heading-lg desktop:text-slideshow-title-md desktop-2xl:text-slideshow-heading-xl desktop-3xl:text-slideshow-heading-2xl',
        ]"
        :style="{ color: accentColorStyle }"
        :data-testid="'slideshowPageCoverTitle'"
      >
        {{ title }}
      </h1>
      <div
        v-if="description"
        :class="[
          'break-words',
          'text-center tablet-landscape:text-start',
          'mx-15 tablet-portrait:mx-47 tablet-landscape:mx-0',
          'text-slideshow-subheading-md tablet-portrait:text-slideshow-subheading-lg desktop:text-slideshow-subheading-xl desktop-2xl:text-slideshow-body-lg desktop-3xl:text-slideshow-body-xl',
          textColorClass,
        ]"
        :data-testid="'slideshowPageCoverDescription'"
      >
        {{ description }}
      </div>
    </div>
  </div>
</template>
